// Shared rounded-corner hex prism geometry, used for both real terrain
// blocks (world/chunks.ts) and the jetty placement ghost preview
// (systems/building.ts). Extracted from legacy/tsuri.html, unchanged.

import * as THREE from 'three';

// Corner rounding, as a fraction of the hex radius. 0.10 was the original
// softened look; this is just enough to break the corner without losing the
// crisp tile edge. At exactly 0 the quadratic curves are skipped entirely
// rather than collapsed to zero-length ones, which would otherwise leave
// degenerate triangles at every corner.
const CORNER_ROUND = 0.02;

// Hex prism (flat-top, first corner along +X to match the axial layout).
// One shared geometry serves every block.
export function buildHexPrism(radius: number, height: number): THREE.ExtrudeGeometry {
  const rad = radius * CORNER_ROUND;
  const corners: THREE.Vector2[] = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i;
    corners.push(new THREE.Vector2(radius * Math.cos(a), radius * Math.sin(a)));
  }
  const shape = new THREE.Shape();
  if (rad <= 0) {
    shape.moveTo(corners[0].x, corners[0].y);
    for (let i = 1; i < 6; i++) shape.lineTo(corners[i].x, corners[i].y);
  } else {
    for (let i = 0; i < 6; i++) {
      const prev = corners[(i + 5) % 6], cur = corners[i], next = corners[(i + 1) % 6];
      const p1 = cur.clone().add(prev.clone().sub(cur).normalize().multiplyScalar(rad));
      const p2 = cur.clone().add(next.clone().sub(cur).normalize().multiplyScalar(rad));
      if (i === 0) shape.moveTo(p1.x, p1.y);
      else shape.lineTo(p1.x, p1.y);
      shape.quadraticCurveTo(cur.x, cur.y, p2.x, p2.y);
    }
  }
  shape.closePath();
  const geo = new THREE.ExtrudeGeometry(shape, { depth: height, bevelEnabled: false, curveSegments: 3 });
  // Remap wall UVs so v runs 0..1 bottom-to-top (extrude leaves v in
  // raw depth units, which would sample only a slice of the texture).
  const wallGroup = geo.groups[1];
  if (wallGroup) {
    const posAttr = geo.attributes.position, uvAttr = geo.attributes.uv;
    for (let i = wallGroup.start; i < wallGroup.start + wallGroup.count; i++) {
      uvAttr.setY(i, posAttr.getZ(i) / height);
    }
  }
  geo.rotateX(-Math.PI / 2);
  geo.translate(0, -height / 2, 0);
  return geo;
}
